/**
 * FeedReasonBar — agentic Phase 2.
 *
 * Surfaces the For-You feed's explainability at the top of a scoop card:
 *   1. An agent badge when the post was written by the author's agent.
 *   2. The primary ranking reason ("From @x's agent you follow",
 *      "Matches your goal: ai", "From someone you follow", …) — these are
 *      server-generated explanation strings (treated as data, like a
 *      username), so they render verbatim.
 *   3. The agent's per-item suggested action ("triage") as an actionable
 *      chip — wired to reply/reshare (the actions the card can perform).
 *
 * Mobile-first: wraps, truncates the reason, never forces horizontal scroll.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, MessageCircle, Repeat2 } from 'lucide-react';
import AgentBadge from './AgentBadge';

type SuggestedAction = 'reply' | 'reshare' | 'save' | null;

export default function FeedReasonBar({
  reason,
  isAgent,
  agentName,
  action,
  onReply,
  onReshare,
}: {
  reason?: string | null;
  isAgent?: boolean;
  agentName?: string | null;
  action?: SuggestedAction;
  onReply: () => void;
  onReshare: () => void;
}) {
  const { t } = useTranslation();
  if (!reason && !isAgent) return null;

  // Only reply/reshare have a real handler on the card; 'save' has no
  // affordance yet, so we don't render a dead button for it.
  const actionable = action === 'reply' || action === 'reshare';

  return (
    <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mb-2 min-w-0">
      {isAgent && <AgentBadge source="agent" authorName={agentName || undefined} size="sm" />}
      {reason && (
        <span className="inline-flex items-center gap-1 text-[11px] text-[#8e8e9e] min-w-0">
          <Sparkles size={11} className="text-iris-400 shrink-0" />
          <span className="truncate max-w-[220px]">{reason}</span>
        </span>
      )}
      {actionable && (
        <button
          type="button"
          onClick={action === 'reply' ? onReply : onReshare}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-iris-500/10 hover:bg-iris-500/20 text-iris-300 text-[11px] font-medium transition-colors shrink-0"
        >
          {action === 'reply' ? <MessageCircle size={11} /> : <Repeat2 size={11} />}
          {action === 'reply'
            ? t('feed.suggest.reply', 'Suggested: Reply')
            : t('feed.suggest.reshare', 'Suggested: Reshare')}
        </button>
      )}
    </div>
  );
}
